// src/pages/admin/CollectionBreakdownCard.jsx
import { useMemo } from "react";
import { Loading } from "@common";
import { refundsSummary } from "@utils/depositSheetColumns";
import { fmtShortDay, formatCad, formatSignedCad } from "./depositMatchUtils";

// How each deposit line's match status colours its cell.
const CELL = {
  matched: {
    cls: "bg-palette-emerald-soft text-palette-emerald-hover",
    label: "Matched",
  },
  variance: {
    cls: "bg-palette-amber-soft text-palette-amber-primary",
    label: "Matched with a difference",
  },
  suggested: {
    cls: "bg-palette-indigo-soft text-palette-indigo-badge",
    label: "Suggested",
  },
  depositPending: {
    cls: "bg-palette-rose-soft text-palette-rose-heading",
    label: "Not in bank",
  },
};
const LEGEND = ["matched", "variance", "suggested", "depositPending"];

/**
 * CollectionBreakdownCard — the selected clinic's Collection Breakdown, one
 * row per day and one column per bank-bound payment type (no Cash / Other),
 * each amount coloured by whether its deposit line is matched. Saves a trip
 * to Deposit Sheet to see what is still open.
 *
 *   records:   Collection Breakdown records ({ date, collection_* })
 *   fields:    [{ key, label }] — the deposit-line columns
 *   practices: Daily Deposits' practice groups; their rows carry each
 *              deposit line's status (deposit ids are "<date>:<field key>")
 */
export default function CollectionBreakdownCard({ records = [], fields, practices, loading }) {
  const statusById = useMemo(() => {
    const map = new Map();
    for (const p of practices) {
      for (const r of p.rows) {
        const status = r.status === "matched" && r.variance ? "variance" : r.status;
        for (const d of r.deposits) map.set(d.id, { status, variance: r.variance });
      }
    }
    return map;
  }, [practices]);

  // Newest first; days with nothing bank-bound are left out.
  const days = useMemo(
    () =>
      [...records]
        .map((rec) => ({
          date: rec.date,
          cells: fields.map((f) => ({ key: f.key, amount: Number(rec[f.key]) || 0 })),
          refund: Number(rec.refund) || 0,
          refundTypes: refundsSummary(rec),
        }))
        .filter((d) => d.cells.some((c) => c.amount !== 0))
        .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0)),
    [records, fields],
  );
  // The Refund column only appears when a day in view has one.
  const hasRefund = days.some((d) => d.refund !== 0);
  const open = days.reduce(
    (n, d) =>
      n +
      d.cells.filter(
        (c) => c.amount !== 0 && statusById.get(`${d.date}:${c.key}`)?.status !== "matched",
      ).length,
    0,
  );

  return (
    <div className="flex min-w-0 flex-col rounded-2xl border border-palette-slate-divider bg-white shadow-sm">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-palette-slate-divider px-4 py-2.5">
        <span className="text-[14.5px] font-semibold text-palette-slate-primary">
          Collection Breakdown
        </span>
        <span className="text-[12px] text-palette-slate-placeholder">
          {days.length} {days.length === 1 ? "day" : "days"}
          {days.length > 0 && ` · ${open} not fully matched`}
        </span>
        <span className="flex-1" />
        <div className="flex flex-wrap gap-x-2.5 gap-y-1">
          {LEGEND.map((k) => (
            <span
              key={k}
              className="inline-flex items-center gap-1 text-[11px] text-palette-slate-secondary"
            >
              <span className={`h-2.5 w-2.5 rounded-sm ${CELL[k].cls}`} />
              {CELL[k].label}
            </span>
          ))}
        </div>
      </div>
      {loading ? (
        <Loading />
      ) : days.length === 0 ? (
        <p className="px-4 py-4 text-sm text-palette-slate-secondary">
          No Collection Breakdown in these dates.
        </p>
      ) : (
        <div className="max-h-[248px] overflow-auto">
          <table className="w-full text-[12.5px] tabular-nums">
            <thead className="sticky top-0 z-10 bg-palette-slate-soft text-[11px] font-semibold uppercase tracking-wider text-palette-slate-placeholder">
              <tr>
                <th className="whitespace-nowrap px-3 py-1.5 text-left">Date</th>
                {fields.map((f) => (
                  <th key={f.key} className="whitespace-nowrap px-2 py-1.5 text-right">
                    {f.label}
                  </th>
                ))}
                {hasRefund && <th className="whitespace-nowrap px-2 py-1.5 text-right">Refund</th>}
                <th className="whitespace-nowrap px-3 py-1.5 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {days.map((d) => (
                <tr key={d.date} className="border-t border-palette-slate-muted">
                  <td className="whitespace-nowrap px-3 py-1 font-semibold text-palette-slate-heading">
                    {fmtShortDay(d.date)}
                  </td>
                  {d.cells.map((c) => {
                    if (c.amount === 0) {
                      return (
                        <td key={c.key} className="px-2 py-1 text-right text-palette-slate-divider">
                          —
                        </td>
                      );
                    }
                    const s = statusById.get(`${d.date}:${c.key}`);
                    const cell = CELL[s?.status] ?? CELL.depositPending;
                    const title =
                      s?.status === "variance"
                        ? `${cell.label}: ${formatSignedCad(s.variance)}`
                        : cell.label;
                    return (
                      <td key={c.key} className="px-1 py-0.5 text-right">
                        <span
                          title={title}
                          className={`inline-block whitespace-nowrap rounded px-1.5 py-0.5 font-semibold ${cell.cls}`}
                        >
                          {formatCad(c.amount)}
                        </span>
                      </td>
                    );
                  })}
                  {hasRefund &&
                    (d.refund === 0 ? (
                      <td className="px-2 py-1 text-right text-palette-slate-divider">—</td>
                    ) : (
                      <td
                        className="whitespace-nowrap px-2 py-1 text-right text-palette-rose-heading"
                        title={
                          d.refundTypes
                            ? `Refunds by method (each taken off that method's deposit): ${d.refundTypes}`
                            : "Refund — no type picked, nothing taken off"
                        }
                      >
                        {formatCad(d.refund)}
                        {d.refundTypes && (
                          <span className="ml-1 text-[11px] text-palette-slate-placeholder">
                            {d.refundTypes}
                          </span>
                        )}
                      </td>
                    ))}
                  <td className="whitespace-nowrap px-3 py-1 text-right font-semibold text-palette-slate-primary">
                    {formatCad(d.cells.reduce((n, c) => n + c.amount, 0))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
